import {Material, MaterialType} from '@domain/materials'
import {PermissionType, RoleBasedPermission} from '@hconnect/apiclient'

export const quarryOptions = [
  'chalkQuarry',
  'clayQuarry',
  'gypsumQuarry',
  'limestoneQuarry',
  'marlQuarry',
  'marlAndChalkQuarry',
  'oilQuarry',
  'puzzolanQuarry',
  'sandQuarry',
  'sandstoneQuarry',
  'otherQuarry',
  'none'
]

export const crusherOptions = [
  'jawCrusher',
  'coneCrusher',
  'gyratoryCrusher',
  'hammerCrusher',
  'horizontalShaftImpactor',
  'verticalShaftImpactor',
  'rollCrusher',
  'sizer',
  'other',
  'none'
]

export const rawOptions = [
  'ballMill',
  'ballMillCombined',
  'crusherAndBallMill',
  'hammerMill',
  'rollerPress',
  'rollerPressAndBallMill',
  'rollerPressAndVertical',
  'verticalBallMill',
  'verticalRollerMill',
  'aerofallMill',
  'horomill',
  'ballMillWet',
  'none'
]

export const carbonCaptureTechnologyOptions = [
  'amineScrubbing',
  'hotPotassiumCarbonate',
  'calciumLoopingTailEnd',
  'temperatureSwingAdsorption',
  'electroSwingAdsorption',
  'cryogenicCapture',
  'membraneTechnology',
  'fullOxyfuelFlueGasRecirculation',
  'fullOxyfuelNoFlueGasRecirculation',
  'partialOxyfuel',
  'integratedCalciumLooping',
  'indirectCalcinationLEILAC',
  'algaeConversion',
  'mineralCarbonation',
  'enforcedCarbonationMineralization'
]

export const carbonCaptureTechnologyOptionsOptional = [...carbonCaptureTechnologyOptions, 'none']

export const separatorOptions = [
  'firstGeneration',
  'secondGeneration',
  'thirdGeneration',
  'vSeparator',
  'combination',
  'openCircuit',
  'none'
]

export const kilnTechnologyOptions = ['dry', 'longDry', 'semiDry', 'wet']

export const kilnPreheaterOptions = [
  'suspensionPreheaterCalciner',
  'suspensionPreheater',
  'gratePreheater',
  'noPreheater',
  'none'
]

export const kilnCalcinerOptions = [
  'airSeparateCombustinChamber',
  'airSeparateInLine',
  'airSeparateInLineWithChamber',
  'airSeparateLine',
  'airThroughInLine',
  'none'
]

export const basicOptions = ['yes', 'no', 'notApplicable']

export const fuelAssignmentForCcuOptions = ['steamGeneration', 'powerGeneration', 'other']

export const copgCategoryOptions = ['fuelFired', 'wind', 'solar', 'otherRenewable']
export const kilnPreaheterStagesOptions = ['_', '2', '3', '4', '5', '6']
export const kilnPreaheterStringsOptions = ['_', '1', '2']
export const kilnSateliteBurnerOptions = ['withSateliteBurner', 'noSateliteBurner', 'none']
export const kilnCoolerTypeOptions = ['rotary', 'satellite', 'grate', 'none']
export const kilnClinkerTypeOptions = ['grey', 'white', 'other', 'none']
export const meshSizeOptions = ['25', '30', '32', '40', '45', '80', '90', '-']
export const co2LoadingFormSiteOptions = ['pipeline', 'vessel', 'railcar', 'truck', 'none']

export type MappedProductMaterial = {
  id: string
  name: string
  localName?: string
  fieldName: ConfigField
}
export const operatingTimeFactorOptions: MappedProductMaterial[] = [
  {id: '1', name: 'pregridingMill', localName: 'pregridingMill', fieldName: 'operatingTimeFactor'},
  {id: '2', name: 'swingMill', localName: 'swingMill', fieldName: 'operatingTimeFactor'},
  {id: '3', name: 'combinedMode', localName: 'combinedMode', fieldName: 'operatingTimeFactor'}
]

export const rawMillOptions: LabelledItem[] = [
  {id: '1', name: 'coalMill'},
  {id: '2', name: 'cementMill'}
]

export const cementMillOptions: LabelledItem[] = [
  {id: '1', name: 'coalMill'},
  {id: '3', name: 'rawMill'}
]

export const coalMillOptions: LabelledItem[] = [
  {id: '2', name: 'cementMill'},
  {id: '3', name: 'rawMill'}
]

export const producedReceivedMaterials: MappedProductMaterial[] = [
  {id: '1', localName: 'Clinker', name: 'Clinker', fieldName: 'storageCapacityClinker'},
  {
    id: '2',
    localName: 'Calcined clay',
    name: 'Calcined clay',
    fieldName: 'storageCapacityCalcinerClay'
  },
  {
    id: '3',
    localName: 'Cement (cli-based)',
    name: 'Cement (cli-based)',
    fieldName: 'storageCapacityCement'
  },
  {id: '4', localName: 'GGBFS', name: 'GGBFS', fieldName: 'storageCapacityGGBFS'},
  {
    id: '5',
    localName: 'Processed fly ash',
    name: 'Processed Fly Ash',
    fieldName: 'storageCapacityProducedFlyAsh'
  },
  {
    id: '6',
    localName: 'Ground pozzolana',
    name: 'Ground Pozzolana',
    fieldName: 'storageCapacityPozzolana'
  },
  {
    id: '7',
    localName: 'Ground limestone',
    name: 'Ground limestone',
    fieldName: 'storageCapacityLimestone'
  }
]

export const specialCementConstituentsOptions: LabelledItem[] = [
  {id: '1', name: 'ferrousSulphate'},
  {id: '2', name: 'stannousSulphate'}
]

export const emissionsOptions: LabelledItem[] = [
  {id: '1', name: 'Dust'},
  {id: '2', name: 'NOx'},
  {id: '3', name: 'SOx'},
  {id: '4', name: 'CO'},
  {id: '5', name: 'NH3'},
  {id: '6', name: 'Hg'},
  {id: '7', name: 'HF'},
  {id: '8', name: 'HCl'},
  {id: '9', name: 'Cd'},
  {id: '10', name: 'Tl'},
  {id: '11', name: 'Sb'},
  {id: '12', name: 'As'},
  {id: '13', name: 'Pb'},
  {id: '14', name: 'Cr'},
  {id: '15', name: 'Co'},
  {id: '16', name: 'Cu'},
  {id: '17', name: 'Mn'},
  {id: '18', name: 'Ni'},
  {id: '19', name: 'V'},
  {id: '20', name: 'Trace metal sum'},
  {id: '21', name: 'VOC/THC/TOC'},
  {id: '22', name: 'D/F'}
]

export type CrusherType = (typeof crusherOptions)[number]
export type BasicOptionType = (typeof basicOptions)[number]
export type FuelAssignmentForCCUSType = (typeof fuelAssignmentForCcuOptions)[number]
export type QuarryType = (typeof quarryOptions)[number]
export type RawType = (typeof rawOptions)[number]
export type SeparatorType = (typeof separatorOptions)[number]
export type CarbonCaptureTechnologyType = (typeof carbonCaptureTechnologyOptionsOptional)[number]
export type MeshSizeType = (typeof meshSizeOptions)[number]
export type OperatingTimeFactorType = (typeof operatingTimeFactorOptions)[number]
export type Co2LoadingFormType = (typeof co2LoadingFormSiteOptions)[number]
export type KilnTechnologyType = (typeof kilnTechnologyOptions)[number]
export type KilnPreheaterType = (typeof kilnPreheaterOptions)[number]
export type KilnCalcinerType = (typeof kilnCalcinerOptions)[number]
export type KilnPreheaterStagesType = (typeof kilnPreaheterStagesOptions)[number]
export type KilnPreheaterStringsType = (typeof kilnPreaheterStringsOptions)[number]
export type KilnSateliteBurnerType = (typeof kilnSateliteBurnerOptions)[number]
export type KilnCoolerType = (typeof kilnCoolerTypeOptions)[number]
export type KilnClinkerType = (typeof kilnClinkerTypeOptions)[number]
export type CopgCategoryType = (typeof copgCategoryOptions)[number]

export type Config = {
  localName: string
  englishName: string
  workCenter?: string
  legacyId?: string
  quarryType: QuarryType
  rawMillType: RawType
  commissStartDate: string
  commissEndDate: string
  useCustomProductionDayBoundaries: string
  productionDayStartTime: string
  productionDayEndTime: string
  mothballed: string
  installedPowerMainDrive: string
  separator: SeparatorType
  meshSize: MeshSizeType
  fuelAssignment: string
  cementAssignment: string
  operatingTimeFactor: string
  carbonCaptureTechnology: CarbonCaptureTechnologyType
  crusherType: CrusherType
  clinkerType: KilnClinkerType
  dailyCapacity: string
  fuelAssignmentMainBurner: string
  fuelAssignmentPreheater: string
  fuelAssignmentOther: string
  technology: KilnTechnologyType
  preheater: KilnPreheaterStagesType
  calciner: KilnCalcinerType
  preheaterStagesNumber: KilnPreheaterStagesType
  preheaterStringsNumber: KilnPreheaterStringsType
  mainBurner: KilnSateliteBurnerType
  cooler: KilnCoolerType
  reliabilityFactorForPss: string
  mtbfTarget: string
  gasBypassRate: string
  copgCategory: CopgCategoryType
  nominalCo2Capture: string
  co2Purity: string
  fuelGenerationForCCUS: FuelAssignmentForCCUSType
  receivedMaterials: string
  producedMaterials: string
  millTypes: string
  specialCementConstituents: string
}
export type InputType =
  | 'Autocomplete'
  | 'Select'
  | 'Date'
  | 'Text'
  | 'Checkbox'
  | 'Numeric'
  | 'TimePicker'

export type ConfigType =
  | 'CementBlending'
  | 'CementMill'
  | 'Crusher'
  | 'Kiln'
  | 'Quarry'
  | 'RawMaterialDeforesting'
  | 'RawMaterialDryer'
  | 'ClayCalcination'
  | 'WHR'
  | 'COPG'
  | 'RawMill'
  | 'CementDryer'
  | 'FuelMaterialsHandling'
  | 'CoalMill'
  | 'CarbonCaptureUtilization'
  | 'SemiFinishedMill'
  | 'Plant'

export const fields: Record<ConfigField, FieldType> = {
  co2Purity: {
    name: 'co2Purity',
    label: 'co2Purity',
    testId: 'co2_purity',
    type: 'Numeric',
    optional: true
  },
  fuelAssignment: {
    name: 'fuelAssignment',
    testId: 'fuel_assignment',
    label: 'fuelAssignment',
    type: 'Select',
    options: [],
    materialType: MaterialType.Fuels,
    optional: true,
    showTooltip: true,
    tooltipKey: 'fuelAssignment'
  },
  producedMaterials: {
    optional: true,
    name: 'producedMaterials',
    testId: 'produced_materials',
    label: 'producedMaterials',
    type: 'Select',
    options: producedReceivedMaterials as unknown as Material[],
    showAllMaterials: true,
    showTooltip: true,
    tooltipKey: 'producedMaterials'
  },
  overrideCoalMillType: {
    name: 'overrideMillType',
    label: 'overrideCoalMillType',
    testId: 'override_mill_types',
    type: 'Select',
    showAllMaterials: true,
    optional: true,
    translateOptions: true,
    options: coalMillOptions as unknown as Material[],
    showTooltip: true,
    tooltipKey: 'multifunctionalMillCoal'
  },
  overrideCementMillType: {
    name: 'overrideMillType',
    label: 'overrideCementMillType',
    testId: 'override_mill_types',
    translateOptions: true,
    type: 'Select',
    optional: true,
    showAllMaterials: true,
    options: cementMillOptions as unknown as Material[],
    showTooltip: true,
    tooltipKey: 'multifunctionalMillCement'
  },
  overrideRawMillType: {
    name: 'overrideMillType',
    testId: 'override_mill_types',
    showAllMaterials: true,
    type: 'Select',
    optional: true,
    translateOptions: true,
    label: 'overrideRawMillType',
    options: rawMillOptions as unknown as Material[],
    showTooltip: true,
    tooltipKey: 'multifunctionalMill'
  },
  overrideMillType: {
    name: 'overrideMillType',
    testId: 'override_mill_types',
    type: 'Select'
  },
  receivedMaterials: {
    name: 'receivedMaterials',
    testId: 'received_materials',
    label: 'receivedMaterials',
    type: 'Select',
    options: producedReceivedMaterials as unknown as Material[],
    showAllMaterials: true,
    optional: true,
    showTooltip: true,
    tooltipKey: 'receivedMaterials'
  },
  cementAssignment: {
    name: 'cementAssignment',
    materialType: [
      MaterialType.IntermediateAndFinishedProducts,
      MaterialType.Cement,
      MaterialType.OtherGroundProductsThanCement
    ],
    classificationCode: 'INTER_FINISHED_SELECTION',
    classificationCodeValue: 'FINISHED_PRODUCT',
    classificationMaterialType: MaterialType.IntermediateAndFinishedProducts,
    testId: 'cement_assignment',
    label: 'cementTypes',
    type: 'Select',
    options: [],
    optional: true,
    showTooltip: true,
    tooltipKey: 'cementAssignment'
  },
  fuelAssignmentMainBurner: {
    name: 'fuelAssignmentMainBurner',
    materialType: MaterialType.Fuels,
    testId: 'fuel_main_burner',
    label: 'fuelAssignmentMainBurner',
    type: 'Select',
    optional: true,
    options: [],
    showTooltip: true,
    tooltipKey: 'fuelAssignmentMainBurner'
  },
  fuelAssignmentVehicles: {
    name: 'fuelAssignmentVehicles',
    materialType: MaterialType.Fuels,
    testId: 'fuel_vehicles',
    label: 'fuelAssignmentVehicles',
    type: 'Select',
    optional: true,
    options: [],
    showTooltip: true,
    tooltipKey: 'fuelAssignmentVehicles'
  },
  fuelAssignmentHeatingSystem: {
    name: 'fuelAssignmentHeatingSystem',
    materialType: MaterialType.Fuels,
    testId: 'fuel_heating_system',
    label: 'fuelAssignmentHeatingSystem',
    type: 'Select',
    optional: true,
    options: [],
    showTooltip: true,
    tooltipKey: 'fuelAssignmentHeatingSystem'
  },
  fuelAssignmentPreheater: {
    materialType: MaterialType.Fuels,
    name: 'fuelAssignmentPreheater',
    testId: 'fuel_pre_heater',
    label: 'fuelAssignmentPreheater',
    type: 'Select',
    optional: true,
    options: [],
    showTooltip: true,
    tooltipKey: 'fuelAssignmentCalciner'
  },
  fuelAssignmentOther: {
    materialType: MaterialType.Fuels,
    name: 'fuelAssignmentOther',
    testId: 'fuel_other',
    label: 'fuelAssignmentOther',
    type: 'Select',
    optional: true,
    options: [],
    showTooltip: true,
    tooltipKey: 'fuelAssignmentOther'
  },
  localName: {
    name: 'localName',
    testId: 'local_name',
    type: 'Text',
    label: 'localName',
    optional: true,
    maxLength: 255,
    showTooltip: true,
    tooltipKey: 'localName'
  },
  englishName: {
    name: 'englishName',
    testId: 'english_name',
    type: 'Text',
    label: 'englishName',
    optional: true
  },
  nominalCo2Capture: {
    name: 'nominalCo2Capture',
    testId: 'nominal_co2_capture',
    type: 'Numeric',
    label: 'nominalCo2Capture',
    optional: true,
    showTooltip: true,
    tooltipKey: 'nominalCo2Capture'
  },
  storageCapacityCement: {
    name: 'storageCapacityCement',
    label: 'storageCapacityCement',
    testId: 'storage_capacity_cement',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true,
    showTooltip: true,
    tooltipKey: 'storageCapacityCement'
  },
  storageCapacityCalcinerClay: {
    name: 'storageCapacityCalcinerClay',
    label: 'storageCapacityCalcinerClay',
    testId: 'storage_capacity_calciner_clay',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true
  },
  storageCapacityClinker: {
    name: 'storageCapacityClinker',
    label: 'storageCapacityClinker',
    testId: 'storage_capacity_clinker',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true,
    showTooltip: true,
    tooltipKey: 'storageCapacityClinker'
  },
  storageCapacityGGBFS: {
    name: 'storageCapacityGGBFS',
    label: 'storageCapacityGGBFS',
    testId: 'storage_capacity_ggbfs',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true,
    showTooltip: true,
    tooltipKey: 'storageCapacityGGBFS'
  },
  storageCapacityLimestone: {
    name: 'storageCapacityLimestone',
    label: 'storageCapacityLimestone',
    testId: 'storage_capacity_limestone',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true
  },
  storageCapacityPozzolana: {
    name: 'storageCapacityPozzolana',
    label: 'storageCapacityPozzolana',
    testId: 'storage_capacity_pozzolana',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true
  },
  storageCapacityProducedFlyAsh: {
    name: 'storageCapacityProducedFlyAsh',
    testId: 'storage_capacity_produced_fly_ash',
    label: 'storageCapacityProducedFlyAsh',
    type: 'Numeric',
    minExclusiveValue: 0,
    optional: true
  },
  mainBurner: {
    name: 'mainBurner',
    testId: 'main_burner',
    type: 'Autocomplete',
    label: 'mainBurner',
    options: kilnSateliteBurnerOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'mainBurner'
  },
  cooler: {
    name: 'cooler',
    testId: 'cooler',
    type: 'Autocomplete',
    label: 'coolerType',
    options: kilnCoolerTypeOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'coolerType'
  },
  copgCategory: {
    name: 'copgCategory',
    testId: 'copgCategory',
    type: 'Autocomplete',
    label: 'copgCategory',
    options: copgCategoryOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'copgCategory'
  },
  fuelAssignmentForCCUS: {
    name: 'fuelAssignmentForCCUS',
    testId: 'fuel_assignment_for_ccus',
    type: 'Autocomplete',
    label: 'fuelAssignment',
    options: fuelAssignmentForCcuOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'fuelAssignmentForCCUS'
  },
  reliabilityFactorForPss: {
    name: 'reliabilityFactorForPss',
    testId: 'reliability_factor',
    type: 'Numeric',
    label: 'reliabilityFactorForPss',
    minExclusiveValue: 0,
    showTooltip: true,
    tooltipKey: 'reliabilityFactorForPss'
  },
  mtbfTarget: {
    name: 'mtbfTarget',
    testId: 'mtbf-target',
    type: 'Numeric',
    label: 'mtbfTarget',
    minExclusiveValue: 0,
    showTooltip: true,
    tooltipKey: 'mtbfTarget'
  },
  gasBypassRate: {
    name: 'gasBypassRate',
    testId: 'gas_bypass',
    type: 'Numeric',
    label: 'gasBypassRate',
    optional: true,
    showTooltip: true,
    tooltipKey: 'gasBypassRate'
  },
  dailyCapacity: {
    name: 'dailyCapacity',
    testId: 'daily_capacity',
    type: 'Numeric',
    label: 'dailyCapacityForPss',
    minExclusiveValue: 0,
    showTooltip: true,
    tooltipKey: 'dailyCapacity'
  },
  quarryType: {
    name: 'quarryType',
    testId: 'type',
    type: 'Autocomplete',
    label: 'type',
    options: quarryOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'quarryType'
  },
  crusherType: {
    name: 'crusherType',
    testId: 'type',
    type: 'Autocomplete',
    label: 'type',
    options: crusherOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'crusherType'
  },
  rawMillType: {
    name: 'rawMillType',
    testId: 'rawMillType',
    type: 'Autocomplete',
    label: 'millType',
    options: rawOptions,
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'rawMillType'
  },
  workCenter: {
    name: 'workCenter',
    testId: 'work_center',
    type: 'Text',
    label: 'workCenter',
    optional: true,
    maxLength: 255,
    showTooltip: true,
    tooltipKey: 'workCenter'
  },
  legacyId: {
    name: 'legacyId',
    testId: 'legacy_id',
    type: 'Text',
    label: 'legacyId',
    optional: true,
    maxLength: 255,
    showTooltip: true,
    tooltipKey: 'legacyId'
  },
  comissionDates: {
    type: 'Date',
    name: 'comissionDates',
    optional: true
  },
  commissStartDate: {
    type: 'Date',
    name: 'commissStartDate'
  },
  commissEndtDate: {
    type: 'Date',
    name: 'commissEndtDate'
  },
  useCustomProductionDayBoundaries: {
    name: 'useCustomProductionDayBoundaries',
    testId: 'useCustomProductionDayBoundaries',
    type: 'Checkbox',
    label: 'useCustomProductionDayBoundaries'
  },
  productionDayStartTime: {
    name: 'productionDayStartTime',
    testId: 'production_day_start_time',
    type: 'TimePicker',
    label: 'productionDayStartTime'
  },
  productionDayEndTime: {
    name: 'productionDayEndTime',
    testId: 'production_day_end_time',
    type: 'TimePicker',
    label: 'productionDayEndTime'
  },
  installedPowerMainDrive: {
    testId: 'installed_power_main_drive',
    name: 'installedPowerMainDrive',
    type: 'Numeric',
    label: 'installedPowerMainDrive',
    optional: true,
    showTooltip: true,
    tooltipKey: 'installedPowerMainDrive'
  },
  nominalElectricalPower: {
    testId: 'nominal_electrical_power',
    name: 'nominalElectricalPower',
    type: 'Numeric',
    label: 'nominalElectricalPower',
    showTooltip: true,
    tooltipKey: 'nominalElectricalPower'
  },
  mothballed: {
    name: 'mothballed',
    testId: 'mothballed',
    type: 'Checkbox',
    label: 'mothballed'
  },
  onSiteSteamGeneration: {
    name: 'onSiteSteamGeneration',
    testId: 'on_site_steam_generation',
    type: 'Checkbox',
    label: 'onSiteSteamGeneration'
  },
  co2FromPowerGeneration: {
    name: 'co2FromPowerGeneration',
    testId: 'co2_from_power_generation',
    type: 'Checkbox',
    label: 'co2FromPowerGeneration'
  },
  co2FromSteamGeneration: {
    name: 'co2FromSteamGeneration',
    testId: 'co2_from_steam_generation',
    type: 'Checkbox',
    label: 'co2FromSteamGeneration'
  },
  separator: {
    name: 'separator',
    label: 'separatorType',
    testId: 'separator',
    options: separatorOptions,
    translateOptions: true,
    type: 'Autocomplete',
    showTooltip: true,
    tooltipKey: 'separatorType'
  },
  technology: {
    name: 'technology',
    label: 'technology',
    testId: 'technology',
    options: kilnTechnologyOptions,
    translateOptions: true,
    type: 'Autocomplete',
    showTooltip: true,
    tooltipKey: 'technology'
  },
  carbonCaptureTechnology1: {
    name: 'carbonCaptureTechnology1',
    label: 'carbonCaptureTechnology',
    testId: 'carbon_capture_technology',
    options: carbonCaptureTechnologyOptions,
    labelParams: {number: 1},
    translateOptions: true,
    type: 'Autocomplete',
    optional: true,
    showTooltip: true,
    tooltipKey: 'carbonCaptureTechnology1'
  },
  carbonCaptureTechnology2: {
    testId: 'carbon_capture_technology2',
    name: 'carbonCaptureTechnology2',
    label: 'carbonCaptureTechnology',
    labelParams: {number: 2},
    options: carbonCaptureTechnologyOptionsOptional,
    translateOptions: true,
    type: 'Autocomplete',
    optional: true,
    showTooltip: true,
    tooltipKey: 'carbonCaptureTechnology2'
  },
  carbonCaptureTechnology3: {
    testId: 'carbon_capture_technology3',
    name: 'carbonCaptureTechnology3',
    labelParams: {number: 3},
    label: 'carbonCaptureTechnology',
    options: carbonCaptureTechnologyOptionsOptional,
    translateOptions: true,
    type: 'Autocomplete',
    optional: true,
    showTooltip: true,
    tooltipKey: 'carbonCaptureTechnology3'
  },
  meshSize: {
    name: 'meshSize',
    label: 'meshSize',
    testId: 'mesh',
    options: meshSizeOptions,
    type: 'Autocomplete',
    showTooltip: true,
    tooltipKey: 'meshSize'
  },
  operatingTimeFactor: {
    name: 'operatingTimeFactor',
    label: 'operatingTimeFactor',
    testId: 'operating_time',
    options: operatingTimeFactorOptions as unknown as Material[],
    type: 'Select',
    translateOptions: true,
    showAllMaterials: true,
    optional: true,
    showTooltip: true,
    tooltipKey: 'operatingTimeFactor'
  },
  emissions: {
    name: 'emissions',
    label: 'emissions',
    testId: 'emissions',
    options: emissionsOptions as unknown as Material[],
    type: 'Select',
    showAllMaterials: true,
    optional: true,
    showTooltip: true,
    tooltipKey: 'emissions'
  },
  clinkerType: {
    name: 'clinkerType',
    label: 'clinkerType',
    testId: 'clinker_type',
    options: kilnClinkerTypeOptions,
    type: 'Autocomplete',
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'clinkerType'
  },
  preheater: {
    name: 'preheater',
    label: 'preheaterType',
    testId: 'preheater_type',
    options: kilnPreheaterOptions,
    type: 'Autocomplete',
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'preheaterType'
  },
  calciner: {
    name: 'calciner',
    label: 'calcinerType',
    testId: 'calciner_type',
    options: kilnCalcinerOptions,
    type: 'Autocomplete',
    translateOptions: true,
    showTooltip: true,
    tooltipKey: 'calcinerType'
  },
  preheaterStagesNumber: {
    name: 'preheaterStagesNumber',
    label: 'preheaterStagesNumber',
    testId: 'preheater_stages',
    options: kilnPreaheterStagesOptions,
    type: 'Autocomplete',
    showTooltip: true,
    tooltipKey: 'preheaterStagesNumber'
  },
  preheaterStringsNumber: {
    name: 'preheaterStringsNumber',
    label: 'preheaterStringsNumber',
    testId: 'preheater_string',
    options: kilnPreaheterStringsOptions,
    type: 'Autocomplete',
    showTooltip: true,
    tooltipKey: 'preheaterStringsNumber'
  },
  co2LoadingFormSite: {
    name: 'co2LoadingFormSite',
    testId: 'co2_loading_form_site',
    type: 'Autocomplete',
    options: co2LoadingFormSiteOptions,
    label: 'co2LoadingFormSite',
    translateOptions: true,
    optional: true
  },
  offShoreStorage: {
    name: 'offShoreStorage',
    testId: 'off_shore_storage',
    type: 'Autocomplete',
    options: basicOptions,
    label: 'offShoreStorage',
    translateOptions: true
  },
  onShoreStorage: {
    name: 'onShoreStorage',
    testId: 'on_shore_storage',
    type: 'Autocomplete',
    options: basicOptions,
    label: 'onShoreStorage',
    translateOptions: true
  },
  co2SentToUse: {
    name: 'co2SentToUse',
    testId: 'co2_sent_to_use',
    type: 'Autocomplete',
    options: basicOptions,
    label: 'co2SentToUse',
    translateOptions: true
  },
  co2OnSiteProcessingUnit: {
    name: 'co2OnSiteProcessingUnit',
    testId: 'co2_onSite_processing_unit',
    type: 'Autocomplete',
    options: basicOptions,
    label: 'co2OnSiteProcessingUnit',
    translateOptions: true
  },
  co2OffSiteProcessingUnit: {
    name: 'co2OffSiteProcessingUnit',
    testId: 'co2_off_site_processing_unit',
    type: 'Autocomplete',
    options: basicOptions,
    label: 'co2OffSiteProcessingUnit',
    translateOptions: true
  },
  specialCementConstituents: {
    name: 'specialCementConstituents',
    testId: 'special_cement_constituents',
    label: 'specialCementConstituents',
    type: 'Select',
    options: specialCementConstituentsOptions as unknown as Material[],
    translateOptions: true,
    showAllMaterials: true,
    optional: true,
    showTooltip: true,
    tooltipKey: 'specialCementConstituents'
  }
}

export type Field = (typeof fields)[ConfigField]

export const commonFields: Field[] = [fields.localName, fields.workCenter, fields.legacyId]

export const configFieldsMap: Record<ConfigType, Field[]> = {
  CementBlending: [...commonFields],
  Plant: [
    {...fields.localName, editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]},
    {
      ...fields.comissionDates,
      editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]
    },
    fields.useCustomProductionDayBoundaries,
    fields.productionDayStartTime,
    fields.productionDayEndTime,
    fields.producedMaterials,
    fields.receivedMaterials,
    fields.storageCapacityCalcinerClay,
    fields.storageCapacityCement,
    fields.storageCapacityClinker,
    fields.storageCapacityLimestone,
    fields.storageCapacityPozzolana,
    fields.storageCapacityGGBFS,
    fields.storageCapacityProducedFlyAsh,
    fields.fuelAssignmentVehicles,
    fields.fuelAssignmentHeatingSystem,
    fields.specialCementConstituents,
    {...fields.mothballed, editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]}
  ],
  CementMill: [
    ...commonFields,
    fields.overrideCementMillType,
    fields.fuelAssignment,
    fields.cementAssignment,
    fields.rawMillType,
    {
      ...fields.comissionDates,
      editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]
    },
    fields.installedPowerMainDrive,
    fields.separator,
    fields.meshSize,
    fields.operatingTimeFactor,
    {...fields.mothballed, editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]}
  ],
  SemiFinishedMill: [
    ...commonFields,
    fields.overrideCementMillType,
    fields.fuelAssignment,
    fields.cementAssignment,
    fields.rawMillType,
    {
      ...fields.comissionDates,
      editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]
    },
    fields.installedPowerMainDrive,
    fields.separator,
    fields.meshSize,
    fields.operatingTimeFactor,
    {...fields.mothballed, editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]}
  ],
  Crusher: [
    ...commonFields,
    fields.crusherType,
    fields.comissionDates,
    fields.installedPowerMainDrive,
    fields.mothballed
  ],
  Kiln: [
    ...commonFields,
    fields.emissions,
    fields.clinkerType,
    fields.fuelAssignmentMainBurner,
    fields.fuelAssignmentPreheater,
    fields.fuelAssignmentOther,
    {
      ...fields.comissionDates,
      editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]
    },
    fields.technology,
    fields.preheater,
    fields.calciner,
    fields.preheaterStagesNumber,
    fields.preheaterStringsNumber,
    fields.mainBurner,
    fields.cooler,
    fields.dailyCapacity,
    fields.reliabilityFactorForPss,
    fields.mtbfTarget,
    fields.gasBypassRate,
    fields.installedPowerMainDrive,
    {...fields.mothballed, editPermissionRequired: [RoleBasedPermission.HproduceRoleCCCSupporter]}
  ],
  Quarry: [...commonFields, fields.quarryType],
  ClayCalcination: [
    ...commonFields,
    fields.fuelAssignment,
    fields.comissionDates,
    fields.mothballed
  ],
  RawMaterialDeforesting: [...commonFields, fields.fuelAssignment],
  RawMaterialDryer: [
    ...commonFields,
    fields.fuelAssignment,
    fields.comissionDates,
    fields.mothballed
  ],
  WHR: [...commonFields],
  COPG: [
    ...commonFields,
    fields.nominalElectricalPower,
    fields.fuelAssignment,
    fields.copgCategory,
    fields.comissionDates,
    fields.mothballed
  ],
  RawMill: [
    ...commonFields,
    fields.overrideRawMillType,
    fields.fuelAssignment,
    fields.rawMillType,
    fields.separator,
    fields.comissionDates,
    fields.installedPowerMainDrive,
    fields.operatingTimeFactor
  ],
  CementDryer: [...commonFields, fields.comissionDates, fields.fuelAssignment, fields.mothballed],
  FuelMaterialsHandling: [...commonFields, fields.fuelAssignment],
  CoalMill: [
    ...commonFields,
    fields.overrideCoalMillType,
    fields.fuelAssignment,
    fields.rawMillType,
    fields.comissionDates,
    fields.installedPowerMainDrive,
    fields.operatingTimeFactor,
    fields.mothballed
  ],
  CarbonCaptureUtilization: [
    ...commonFields,
    fields.carbonCaptureTechnology1,
    fields.carbonCaptureTechnology2,
    fields.carbonCaptureTechnology3,
    fields.nominalCo2Capture,
    fields.co2LoadingFormSite,
    fields.co2Purity,
    fields.fuelAssignmentForCCUS,
    fields.offShoreStorage,
    fields.onShoreStorage,
    fields.co2SentToUse,
    fields.co2OnSiteProcessingUnit,
    fields.co2OffSiteProcessingUnit,
    fields.onSiteSteamGeneration,
    fields.co2FromSteamGeneration,
    fields.co2FromPowerGeneration
  ]
}

export const configFields = [
  'localName',
  'englishName',
  'workCenter',
  'legacyId',
  'fuelAssignment',
  'cementAssignment',
  'fuelAssignmentForCCUS',
  'crusherType',
  'quarryType',
  'comissionDates',
  'commissStartDate',
  'commissEndtDate',
  'useCustomProductionDayBoundaries',
  'productionDayStartTime',
  'productionDayEndTime',
  'installedPowerMainDrive',
  'mothballed',
  'separator',
  'meshSize',
  'operatingTimeFactor',
  'clinkerType',
  'dailyCapacity',
  'fuelAssignmentMainBurner',
  'fuelAssignmentHeatingSystem',
  'fuelAssignmentVehicles',
  'fuelAssignmentPreheater',
  'fuelAssignmentOther',
  'technology',
  'preheater',
  'calciner',
  'preheaterStagesNumber',
  'preheaterStringsNumber',
  'mainBurner',
  'cooler',
  'reliabilityFactorForPss',
  'mtbfTarget',
  'gasBypassRate',
  'copgCategory',
  'nominalElectricalPower',
  'rawMillType',
  'carbonCaptureTechnology1',
  'carbonCaptureTechnology2',
  'carbonCaptureTechnology3',
  'nominalCo2Capture',
  'onSiteSteamGeneration',
  'co2FromSteamGeneration',
  'co2FromPowerGeneration',
  'co2LoadingFormSite',
  'co2Purity',
  'offShoreStorage',
  'onShoreStorage',
  'co2SentToUse',
  'co2OnSiteProcessingUnit',
  'co2OffSiteProcessingUnit',
  'producedMaterials',
  'receivedMaterials',
  'emissions',
  'storageCapacityCement',
  'storageCapacityCalcinerClay',
  'storageCapacityClinker',
  'storageCapacityGGBFS',
  'storageCapacityProducedFlyAsh',
  'storageCapacityPozzolana',
  'storageCapacityLimestone',
  'overrideCoalMillType',
  'overrideCementMillType',
  'overrideRawMillType',
  'overrideMillType',
  'specialCementConstituents'
] as const

export type ConfigField = (typeof configFields)[number]

export type FieldType = {
  name: ConfigField
  testId?: string
  type: InputType
  options?: string[] | Material[]
  label?: string
  optional?: boolean
  labelParams?: {[key: string]: number | string}
  startDate?: string
  endDate?: string
  translateOptions?: boolean
  showAllMaterials?: boolean
  materialType?: MaterialType | MaterialType[]
  classificationCode?: string
  classificationCodeValue?: string
  classificationMaterialType?: MaterialType
  editPermissionRequired?: PermissionType[]
  minExclusiveValue?: number
  maxLength?: number
  showTooltip?: boolean
  tooltipKey?: string
}

export type LabelledItem = {
  id: string
  name: string
}
